/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { FieldPath, UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { TableCell } from "@/components/ui/table";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { cn } from "@/lib/utils";

export const TableCellTextInput = <T extends z.ZodTypeAny>({
  className,
  disabled,
  fieldName,
  form,
  handleOnBlur,
  index,
  listName,
  rowId,
  value,
}: {
  className?: string;
  disabled?: boolean;
  fieldName: string;
  form: UseFormReturn<z.infer<T>>;
  handleOnBlur: (
    id: number,
    fieldName: string,
    updatedValue: string | null,
    type?: "date" | "number",
  ) => void;
  index: number;
  listName: string;
  rowId: number;
  value?: string; // For disabled display-only fields
}) => {
  // If disabled and value provided, render as display-only
  if (disabled && value !== undefined) {
    return (
      <TableCell className="p-1">
        <Input
          id={`cell-${fieldName}-${index}`}
          className={cn(
            "truncate rounded-sm border-transparent bg-transparent text-slate-500",
            className,
          )}
          disabled={true}
          placeholder="-"
          value={value}
        />
      </TableCell>
    );
  }

  const fieldNamePath = `${listName}.${index}.${fieldName}` as FieldPath<
    z.infer<T>
  >;

  return (
    <TableCell className="p-1">
      <FormField
        control={form.control}
        name={fieldNamePath}
        render={({ field }) => {
          return (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  id={`cell-${fieldName}-${index}`}
                  className="truncate rounded-sm border-transparent bg-transparent hover:border-slate-300 focus:border-slate-300"
                  disabled={disabled}
                  placeholder="-"
                  value={field.value as string}
                  onBlur={() => {
                    void form
                      .trigger(fieldNamePath, { shouldFocus: false })
                      .then((ok) => {
                        if (!ok) return;
                        handleOnBlur(
                          rowId,
                          fieldName,
                          field.value ? (field.value as string) : null,
                        );
                        mixpanelUtils.trackInlineEdit(
                          fieldName,
                          getScreenFromPathname(location.pathname),
                        );
                      });
                  }}
                />
              </FormControl>
            </FormItem>
          );
        }}
      />
    </TableCell>
  );
};
